// ─── My First Project — Onboarding Sample Seed ───
//
// A 1-work-week, self-paced sample project that auto-seeds for every new
// signed-in user. Entirely assigned to the user themselves (no fake teammates).
// Tasks are flagged is_sample=true so they are excluded from instance/org
// analytics, dashboards, and rollups.
//
// Voice: warm, plain English. Approachable for users with zero PM background,
// while still respecting seasoned PMs by referencing real concepts (action
// items, dependencies, signal-based tracking) without jargon overload.

export const MY_FIRST_PROJECT_NAME = 'My First Project';

export interface SeedActionItem {
  text: string;
}

export interface SeedSampleTask {
  /** Stable key used to wire parent_id within the seed without UUIDs */
  key: string;
  /** Optional parent key (must reference another task in the same seed) */
  parentKey?: string;
  /** Optional FS predecessor key (must reference another task in the same seed) */
  predecessorKey?: string;
  name: string;
  intent: string;
  specs?: string[];
  /** Workdays this task spans (1 = a single 8-hour day) */
  durationDays: number;
  /** 0-indexed offset from project start (workdays). undefined = sequential after previous sibling */
  startOffsetDays?: number;
  actionItems: SeedActionItem[];
}

/**
 * 5-workday curriculum. Each parent task is one day, with optional subtasks
 * that cover the day's topics in finer detail.
 */
export const MY_FIRST_PROJECT_TASKS: SeedSampleTask[] = [
  // ───────── TLP — My First Project ─────────
  {
    key: 'tlp',
    name: MY_FIRST_PROJECT_NAME,
    intent:
      'A 1-week guided tour of Workitect. Each day below is a focused lesson; complete them in order — they are linked Finish-to-Start.',
    specs: ['5 daily lessons', 'Self-paced', 'Safe to delete when done'],
    durationDays: 5,
    startOffsetDays: 0,
    actionItems: [],
  },

  // ───────── DAY 1 — Get Oriented ─────────
  {
    key: 'day1',
    parentKey: 'tlp',
    name: 'Day 1 — Get Oriented',
    intent:
      'Welcome! Today is about finding your way around. By the end you will know where your work lives, how to read the timeline, and how to ask the assistant for help.',
    specs: ['Tour the dashboard', 'Read the Gantt timeline', 'Meet TaskBot (the AI assistant)'],
    durationDays: 1,
    startOffsetDays: 0,
    actionItems: [
      { text: 'Open the **Dashboard** from the left sidebar — this is your home base for what needs your attention today.' },
      { text: 'Switch to the **Gantt** view. The horizontal bars are tasks; the lines between them are dependencies.' },
      { text: 'Click any task bar once to zoom to it. Double-click to open its full details panel.' },
      { text: 'Open the chat panel and ask TaskBot: "What should I work on?"' },
      { text: 'Find your **profile menu** (top-right) and confirm your display name and theme.' },
      { text: 'Open the **Help menu** and start the "Welcome Tour".' },
      { text: 'Hover the toolbar icons to learn what each one does.' },
      { text: 'Note where the **search**, **notifications**, and **add task** buttons live.' },
    ],
  },

  // ───────── DAY 2 — Tasks & Action Items ─────────
  {
    key: 'day2',
    parentKey: 'tlp',
    predecessorKey: 'day1',
    name: 'Day 2 — Tasks & Action Items',
    intent:
      'Tasks are the unit of work. Action items are the small steps inside them. Today you will open a task, edit it, check things off, and create one of your own.',
    specs: ['Open & edit tasks', 'Use action items', 'Create your own task'],
    durationDays: 1,
    startOffsetDays: 1,
    actionItems: [
      { text: 'Double-click this task to open the **detail panel**.' },
      { text: 'Read the **Intent** field — every task should say *why* it exists, not just *what* it is.' },
      { text: 'Check off the action items below as you do them — your progress fills in automatically.' },
      { text: 'Try the **Working On It** button. It nudges progress forward when you are mid-flight.' },
      { text: 'Notice there are no "In Progress / Done" status columns — Workitect tracks **signals**, not labels.' },
      { text: 'Click **+ Add Task** in the toolbar (or use the keyboard shortcut shown on hover) to create your own task.' },
      { text: 'Give it a name, a one-sentence **intent**, and 2-3 action items — short, verb-first phrases like "Email Sam".' },
      { text: 'Assign it to yourself, pick a due date, then complete one action item and watch the progress bar move.' },
    ],
  },

  // ───────── DAY 3 — Dependencies & Scheduling ─────────
  {
    key: 'day3',
    parentKey: 'tlp',
    predecessorKey: 'day2',
    name: 'Day 3 — Dependencies & Scheduling',
    intent:
      'Real work has order. Today you will link tasks together, see what blocks what, learn how Workitect reschedules things for you, and set priority and milestones.',
    specs: ['Link predecessors', 'Trust fluid scheduling', 'Priority & milestones'],
    durationDays: 1,
    startOffsetDays: 2,
    actionItems: [
      { text: 'In the Gantt, drag from the right edge of one task bar to the left edge of another — that creates a **predecessor** link.' },
      { text: 'If you move a task earlier than its predecessor allows, Workitect will warn you (the AI calls this a **boundary violation**).' },
      { text: 'Open the detail panel and look at the **Dependencies** section — you can add or remove links here too.' },
      { text: 'Drag a task bar to a new date. Anything downstream shifts with it automatically.' },
      { text: 'For seasoned PMs: this is **fluid scheduling** — like CPM but it negotiates with you instead of breaking.' },
      { text: 'Open any task and set **Priority** (Low / Medium / High / Urgent).' },
      { text: 'Convert a key task into a **milestone** — it becomes a diamond on the timeline.' },
      { text: 'Notice how urgent + milestone tasks bubble to the top of your dashboard.' },
    ],
  },

  // ───────── DAY 4 — Collaboration ─────────
  {
    key: 'day4',
    parentKey: 'tlp',
    predecessorKey: 'day3',
    name: 'Day 4 — Collaboration',
    intent:
      'Work is a team sport, even when the team is just you and one other person. Today covers comments, mentions, and getting approval.',
    specs: ['@mentions', 'Comments & activity', 'Send for approval'],
    durationDays: 1,
    startOffsetDays: 3,
    actionItems: [
      { text: 'Open a task and add a **comment**. Type `@` to mention someone — they will get a notification.' },
      { text: 'Look at the **Activity** tab — every change is logged so nobody has to guess what happened.' },
      { text: 'Try **Share** on a task to send a read-only link to someone outside Workitect.' },
      { text: 'Use **Send for Approval** when work is ready for sign-off. Approvers can comment and request changes without an account.' },
      { text: 'For Managers+: explore the **Approvals** dashboard to see everything pending sign-off.' },
    ],
  },

  // ───────── DAY 5 — Wrap & Explore ─────────
  {
    key: 'day5',
    parentKey: 'tlp',
    predecessorKey: 'day4',
    name: 'Day 5 — Wrap & Explore',
    intent:
      'You have the basics. Today is a quick wrap-up plus a peek at the features — and the role-specific screens — that make Workitect worth coming back to.',
    specs: ['Daily Brief & Digest', 'Blueprints', 'Your role-specific superpowers'],
    durationDays: 1,
    startOffsetDays: 4,
    actionItems: [
      { text: 'Read the **Daily Brief** the AI prepared for you (top of dashboard). It learns what you care about over time.' },
      { text: 'Browse the **Blueprint Gallery** — these are reusable project templates you can launch with one click.' },
      { text: 'Open **Settings → Notifications** and set how you want to hear from us (email, push, in-app).' },
      { text: 'Try **Intake Forms** — AI-built request forms you can share with anyone. Submissions land in your inbox.' },
      { text: '**Owners & Admins:** check the **Team** panel to invite people and set up groups.' },
      { text: '**Managers:** open the **Workload** view to see who is stretched and who has capacity.' },
      { text: '**Regular Users:** the dashboard is your home — everything you need is one click from there.' },
      { text: 'Every role can ask TaskBot: "What can I do that I have not tried yet?"' },
      { text: 'When you are done with this sample, you can **delete it** from the project menu — it never counted toward your real metrics anyway.' },
    ],
  },
];

